import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PlycaDataService } from '../../plyca.service';
import { SignaturePasswordAuthComponent } from '../components/signature-password.component';
import { UserService } from '../../../app/user.service';
import { WaitLoadingService } from '../../../app/components/common/waitLoading/wait-loading.service';
import { AuthenticatedProviderConfig, AuthenticatedSignatureProvider, SignatureAuthorizationResponse } from './authenticated.signature-provider';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { SignatureUtils } from '../../signature.utils';
import { map, switchMap } from 'rxjs/operators';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { Configuration } from '../../../app/conf/app.constants';
import { FileDetailsDTO } from '../../../app/api/plyca-dtos';
import { SignatureStatus, SignatureRequest, Certificate, SignatureProviderType } from '../signature.objects';
import { Injectable } from '@angular/core';


class SignatureAuthorization {
  token: string;
}
class SignatureCertificate {
  identifier: String;
  certificateB64: String;
  validFrom: Date;
  validTo: Date;
  subject: string;
  issuer: string;
  password: string;
}

class SignatureResponse {
  file: FileDetailsDTO;
}

export class PasswordProviderConfig extends AuthenticatedProviderConfig {
  /**
   * Mostrar usuario en la ventana de identificacion con el proveedor de seguridad
   */
  userHidden: boolean;
  /**
   * Permitir modificar usuario en la ventana de identificacion con el proveedor de seguridad
   */
  userNotEditable: boolean;
}

/**
 * Clase asociada a un proveedor de firma externo que requiere de identificacion previa
 * del usuario mediante usuario y contrasenya
 */
@Injectable({
  providedIn: 'root'
})
export abstract class PasswordSignatureProvider extends AuthenticatedSignatureProvider {

  constructor(plycaService: PlycaDataService, translateService: TranslateService, userService: UserService,
    dialogService: DialogService, waitService: WaitLoadingService, signatureUtils: SignatureUtils
    , plycaErrorHandler: PlycaErrorHandler, configuration: Configuration) {

    super(plycaService, translateService, userService, dialogService, waitService, signatureUtils, plycaErrorHandler, configuration);
  }

  /**
   * Metodo al que invocara la ventana de solicitud de usuario y contrasenya
   * @param user identificador de usuario introducido
   * @param password constrasenya de usuario introducido
   */
  public basicAutentication(user: string, password: string): Observable<SignatureAuthorizationResponse> {
    const authReq = {
      'userName': user,
      'password': password
    }
    // invocar a servidor para obtener credenciales
    return this.plycaService.simplePost(this.configuration.ServerWithPublicApiUrl + 'integrations/signatures/basicAuth', authReq).pipe(
      map((response: SignatureAuthorization) => {
        const auth = new SignatureAuthorizationResponse();
        auth.status = SignatureStatus.OK;
        auth.authorization = response.token;
        return auth;
      })
    );
  }

  protected authenticateInternal(): Observable<SignatureAuthorizationResponse> {
    this.waitService.cancelWaiting();
    return of({}).pipe(
      switchMap(() => {
        const config: DynamicDialogConfig = {
          showHeader: true,
          header: 'Identificación',
          width: '40%',
          data: {
            authProvider: this
          }
        };
        // abrir ventana de solicitud de credenciales que realizara el login
        const dialogRef = this.dialogService.open(SignaturePasswordAuthComponent, config);
        return dialogRef.onClose;
      })
    )
  }

  /**
   *  Metodo de obtencion del identificador de usuario en el proveedor de firma
   * @returns
   */
  public getProviderUserId(): string {
    return this.userService.getUser().identifier;
  }

  protected signInternal(request: SignatureRequest, authorization: any): Observable<FileDetailsDTO> {
    const signhReq = {
      'token': authorization,
      'file': request.file,
      'certificate': request.certificate,
      'documentClass': request.documentClass,
      'comment': request.comment
    }
    return this.plycaService.simplePost(this.configuration.ServerWithUserApiUrl + 'integrations/signatures/sign', signhReq).pipe(
      map((signatureResponse: SignatureResponse) => signatureResponse.file)
    )
  }

  public getCertificateList(): Observable<Certificate[]> {
    const authReq = {
      'token': this.getAuthorizationResponse()
    }
    return this.plycaService.simplePost(this.configuration.ServerWithUserApiUrl + 'integrations/signatures/certificates', authReq);
  }

  public check(): Observable<SignatureStatus> {
    return of(SignatureStatus.OK);
  }

  protected getSignatureProviderType(): SignatureProviderType {
    return SignatureProviderType.PASSWORD_PROVIDER;
  }

}
